import { Injectable, Inject, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { AuthService } from './services/auth/auth.service';
import { SHARED_LIB_CONFIG } from './config/shared-lib-config.token';
import { SharedLibConfig } from './config/shared-lib-config';
import { UsedSgkz011aService } from './services/used-sgkz011a.service';
import { catchError, takeUntil } from 'rxjs/operators';
import { ResponseCommon } from 'sg-api';
import { AuthSessionInterceptorService } from './auth-session-interceptor.service';
import { SystemErrorDataService } from './components/sgkz002d/services/system-error.service';
import { SoshikiKenmuInfo } from './components/sgkz007d/models/soshikiKenmuInfo.model';
import { UsedSgkz012aService } from './services/used-sgkz012d.service';
import { LoginUserIdService } from './services/login-user-id.service';

/**
 * HTTPリクエスト実行前に認証情報を更新し、取得したトークンをヘッダに設定する
 * @export
 * @class AuthSessionInterceptor
 * @implements {HttpInterceptor}
 */
@Injectable({
  providedIn: 'root'
})
export class AuthSessionInterceptor implements HttpInterceptor {

  /**
   * コンストラクタ
   * @param {AuthService} authService
   * @memberof AuthSessionInterceptor
   */
  constructor(private systemErrorDataService: SystemErrorDataService,
    private authSessionInterceptorService: AuthSessionInterceptorService,
    private usedSgkz012aService: UsedSgkz012aService,
    private loginUserIdService: LoginUserIdService,
    @Inject(SHARED_LIB_CONFIG) private sharedLibConfig: SharedLibConfig
  ) { }

  /**
   * インターセプト処理.トークンをヘッダに設定
   *
   * @param {HttpRequest<any>} request
   * @param {HttpHandler} next
   * @return {*}  {Observable<HttpEvent<any>>}
   * @memberof AuthSessionInterceptor
   */
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    // 対象のurlと一致しなければそのまま
    if (!this.sharedLibConfig.authInterceptTargetUrl.find(x => new RegExp(x).test(request.url))) {
      return next.handle(request);
    }

    const contentType = request.headers.get('Content-Type');
    if (contentType === 'application/json') {
      // リクエストにユーザ情報を付与
      this.setLoginUserInfo(request);
    }

    const jwtToken = inject(AuthService).getIdToken();

    let userId = '';
    this.loginUserIdService.loginUserId.subscribe(loginUserId => {
      userId = loginUserId;
    });

    let authReq;
    if (userId !== null && userId !== '') {
      authReq = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${jwtToken}`).set('X-User-ID', userId),
      });
    } else {
      authReq = request.clone({
        headers: request.headers.set('Authorization', `Bearer ${jwtToken}`)
      });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {

        let resp = null;
        if (error.error != null && error.error.responseCommon != null) {
          resp = error.error.responseCommon as ResponseCommon;
        }

        // リクエストカスタマイズで組織兼務情報を取得するのは、401エラーの場合のみ
        // 後に組織兼務情報取得APIを呼び出して、データを取得している
        if (error.status === 401) {

          let soshikiKenmuInfoList: SoshikiKenmuInfo[] = [];
          for (const soshikiKenmuInfo of resp.responseCustomize.soshikiKenmuInfo) {

            let soshikiKenmu: SoshikiKenmuInfo = {
              userId: soshikiKenmuInfo.ocHUsrId,
              organizationName: soshikiKenmuInfo.ocHSosikiName,
              companyName: soshikiKenmuInfo.ocYCmpnyName,
              userName: soshikiKenmuInfo.ocHUsrKanjiName
            };

            soshikiKenmuInfoList.push(soshikiKenmu);
          }

          this.usedSgkz012aService.soshikiKenmuInfo = soshikiKenmuInfoList;
        }

        if (error.status >= 500 && 599 >= error.status) {
          this.authSessionInterceptorService.notifySystemError();

          let isErrorEmpty = resp === null || resp.errors.length === 0;
          if (!isErrorEmpty) {
            this.setErrorItem(resp.errors[0].errorCode, resp.errors[0].message, error.status + ': ' + error.statusText);
          }

          if (isErrorEmpty) {
            this.setErrorItem('', 'サーバーでエラーが発生しました。', error.status + ': ' + error.statusText);
          }
        }

        return throwError(error);
      })
    );
  }

  /**
   * リクエストカスタマイズエリアにユーザ情報を付与する。
   *
   * @param {HttpRequest<any>} request
   * @memberof AuthSessionInterceptor
   */
  private setLoginUserInfo(request: HttpRequest<any>): void {

    const usedSgkz011aService = inject(UsedSgkz011aService);
    let info = {
      'loginUserInfo': {
        companyGroupCode: '',
        companyCode: '',
        companyName: '',
        sosikiId: '',
        sosikiName: '',
        userName: ''
      }
    };

    usedSgkz011aService.userOrganizationInfo.subscribe(userOrganizationInfo => {
      info.loginUserInfo.companyGroupCode = userOrganizationInfo.comgrpcd;
      info.loginUserInfo.companyCode = userOrganizationInfo.comCode;
      info.loginUserInfo.companyName = userOrganizationInfo.cmpnyName;
      info.loginUserInfo.sosikiId = userOrganizationInfo.sosikiId;
      info.loginUserInfo.sosikiName = userOrganizationInfo.sosikiName;
    });

    usedSgkz011aService.userKanjiname.subscribe(userKanjiname => {
      info.loginUserInfo.userName = userKanjiname;
    });

    let requestCustomize: any = request.body.requestCommon.requestCustomize;
    request.body.requestCommon.requestCustomize = Object.assign(requestCustomize, info);
  }

  /**
   * システムエラー画面表示項目を設定。
   *
   * @param {HttpRequest<any>} request
   * @memberof AuthSessionInterceptor
   */
  private setErrorItem(errorCode: string, message: string, errorDetail: string): void {
    this.systemErrorDataService.systemErrorData = {
      messageId: errorCode,
      message: message,
      errorDetail: errorDetail,
    }
  }
}